import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceAPI, formatCurrency } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Search, FileText, Eye, Edit, Trash2, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { formatDateDisplay } from '../lib/dateUtils';
import BranchSelect from '../components/BranchSelect';

export default function InvoiceList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    branch_id: searchParams.get('branch_id') || '',
    page: parseInt(searchParams.get('page')) || 1
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: '', number: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await invoiceAPI.list(params);
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Persist filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== '' && !(k === 'page' && v === 1)) params.set(k, v); });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const downloadPDF = async (id, num) => {
    try {
      const res = await invoiceAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${num}.pdf`; a.click();
      toast.success('PDF downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handleDelete = async () => {
    try { await invoiceAPI.delete(deleteDialog.id); toast.success('Deleted'); setDeleteDialog({ open: false, id: '', number: '' }); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="invoice-list-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Tax Invoices</h1>
          <p className="text-sm text-slate-500 font-body mt-1">{total} invoices</p>
        </div>
        <button onClick={() => navigate('/invoices/new')} className="btn-accent flex items-center gap-2" data-testid="new-invoice-btn">
          <Plus className="w-4 h-4" /> New Invoice
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setFilters(f => ({ ...f, page: 1 })); }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 glass-card rounded-xl p-2.5">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-purple-400" />
          <input type="text" placeholder="Search..." value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} className="form-input pl-9 py-2 text-xs w-full" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value, page: 1 }))} className="form-input min-w-0 flex-1 sm:w-32 py-2 text-xs" data-testid="date-from" />
          <span className="text-purple-300 text-xs shrink-0">to</span>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value, page: 1 }))} className="form-input min-w-0 flex-1 sm:w-32 py-2 text-xs" data-testid="date-to" />
          <BranchSelect allowAll value={filters.branch_id} onChange={(v) => setFilters(f => ({ ...f, branch_id: v, page: 1 }))} className="w-40 py-2 text-xs" testId="invoice-branch-filter" />
          <button type="submit" className="btn-primary py-2 px-4 text-xs whitespace-nowrap shrink-0" data-testid="search-btn">Filter</button>
        </div>
      </form>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="invoices-table">
              <thead><tr><th>S.No.</th><th>Invoice #</th><th>Type</th><th>Date</th><th>Party</th><th className="text-right">Grand Total</th><th>Created By</th><th className="text-center">Actions</th></tr></thead>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_number}`}>
                    <td className="text-slate-500 text-sm">{(filters.page - 1) * 20 + idx + 1}</td>
                    <td className="font-mono font-medium text-slate-900">{inv.invoice_number}</td>
                    <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${inv.voucher_type === 'purchase' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{inv.voucher_type === 'purchase' ? 'Purchase' : 'Sales'}</span></td>
                    <td className="text-slate-500 text-sm">{formatDateDisplay(inv.invoice_date)}</td>
                    <td className="text-sm">{inv.billed_to_name}</td>
                    <td className="text-right font-mono font-medium">Rs.{formatCurrency(inv.grand_total)}</td>
                    <td className="text-xs text-slate-500">{inv.created_by_name}</td>
                    <td>
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        <button onClick={() => navigate(`/invoices/${inv.invoice_id}`)} className="p-1.5 hover:bg-slate-100 rounded-md" aria-label="View"><Eye className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button onClick={() => navigate(`/invoices/${inv.invoice_id}/edit`)} className="p-1.5 hover:bg-slate-100 rounded-md" aria-label="Edit"><Edit className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button onClick={() => downloadPDF(inv.invoice_id, inv.invoice_number)} className="p-1.5 hover:bg-slate-100 rounded-md" aria-label="Download PDF"><FileText className="w-3.5 h-3.5 text-orange-600" /></button>
                        {isAdmin && <button onClick={() => setDeleteDialog({ open: true, id: inv.invoice_id, number: inv.invoice_number })} className="p-1.5 hover:bg-rose-50 rounded-md" aria-label="Delete"><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-12">No invoices found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200"><p className="text-sm text-slate-500">Page {filters.page} of {pages}</p><div className="flex gap-2"><button onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))} disabled={filters.page <= 1} className="btn-secondary px-3 py-1.5 text-xs"><ChevronLeft className="w-4 h-4" /></button><button onClick={() => setFilters(f => ({ ...f, page: Math.min(pages, f.page + 1) }))} disabled={filters.page >= pages} className="btn-secondary px-3 py-1.5 text-xs"><ChevronRight className="w-4 h-4" /></button></div></div>}
      </div>

      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, id: '', number: '' })}>
        <DialogContent><DialogHeader><DialogTitle>Delete Invoice</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Delete <strong>{deleteDialog.number}</strong>?</p>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: '', number: '' })}>Cancel</Button><Button variant="destructive" onClick={handleDelete}>Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
